/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { SoundingsCollection, SourceSelection } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import { uiSelectors, uiTypes, useSetupDialog } from '@opengeoweb/store';
import { SoundingsSourceMenu } from './SoundingsSourceMenu';
import { useGetCollectionsInstances } from '../../api/edr-api/hooks';
import { soundingsActions } from '../../store/reducer';
import {
  getSoundingsCollections,
  getSourceSelections,
} from '../../store/selectors';
import type { SoundingsRootState } from '../../store/types';
import { SoundingsActionOrigin } from '../../store/types';

interface SoundingsSourceDialogConnectProps {
  soundingsCollections: SoundingsCollection[] | undefined;
}

export const SoundingsSourceDialogConnect = ({
  soundingsCollections,
}: SoundingsSourceDialogConnectProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { isDialogOpen, onCloseDialog, dialogOrder, setDialogOrder } =
    useSetupDialog(uiTypes.DialogTypes.SoundingsSources);

  const viewId = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, uiTypes.DialogTypes.SoundingsSources),
  );

  const updateCollection = React.useCallback(
    (sourceId: string, value: SourceSelection) => {
      dispatch(
        soundingsActions.updateSelectedSource({
          sourceId,
          value,
          viewId,
          origin: SoundingsActionOrigin.soundingsComponent,
        }),
      );
    },
    [dispatch, viewId],
  );

  const soundingsCollectionsFromStore = useSelector(
    (state: SoundingsRootState) => getSoundingsCollections(state),
  );
  const collections = soundingsCollections ?? soundingsCollectionsFromStore;

  const sourceSelections = useSelector((state: SoundingsRootState) =>
    getSourceSelections(state, viewId),
  );

  // Set mappings for each collection
  React.useEffect(() => {
    if (soundingsCollections && soundingsCollections.length > 0) {
      dispatch(soundingsActions.setSoundingsCollections(soundingsCollections));
    }
  }, [dispatch, soundingsCollections]);

  React.useEffect(() => {
    collections?.forEach((collection) => {
      const currentSelection = sourceSelections[collection.id];

      if (currentSelection?.url && currentSelection?.parameterMappings) {
        return;
      }

      const newValue = {
        url: collection.url,
        isChecked: currentSelection?.isChecked || false,
        parameterMappings: collection.parameterMappings,
        windSpeedUnit: collection.windSpeedUnit,
        ...(collection.name && collection.name !== ''
          ? { name: collection.name }
          : {}),
      };

      dispatch(
        soundingsActions.updateSelectedSource({
          sourceId: collection.id,
          value: newValue,
          viewId,
          origin: SoundingsActionOrigin.soundingsManager,
        }),
      );
    });
  }, [dispatch, collections, viewId, sourceSelections]);

  const { data, isPending, error } = useGetCollectionsInstances(
    collections,
    isDialogOpen,
  );

  return (
    <SoundingsSourceMenu
      error={error}
      handleClose={onCloseDialog}
      isLoading={isPending}
      isOpen={isDialogOpen}
      onMouseDown={setDialogOrder}
      order={dialogOrder}
      soundingsCollections={data}
      sourceSelections={sourceSelections}
      updateCollection={updateCollection}
    />
  );
};
